import React from "react";
import { SearchBar } from "@/components/common/SearchBar";
import { SectionRule } from "components/common/SectionRule";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import type { RowContextMenu } from "@/components/hooks/useContextMenu";
import { OpeningSpinner } from "./OpeningSpinner";

/// A list hint's type, for a hint laid out by its caller (a dialog section
/// has no icon for `HintRow`'s slot to line up with).
export const HINT_TEXT = "text-small text-fg-muted italic";

/// The muted one-row line a list shows in place of rows — its first-run hint,
/// or a spinner until the first fetch lands. One footprint, so nothing jumps.
export const HintRow: React.FC<{
  loading?: boolean;
  children: React.ReactNode;
}> = ({ loading, children }) => (
  <div
    role={loading ? "status" : undefined}
    className={`flex items-center gap-2 item-height px-1 ${HINT_TEXT}`}
  >
    {/* The w-5 slot aligns the text with the section label, not its icon. */}
    <span className="w-5 shrink-0 flex justify-center" aria-hidden>
      {loading && <OpeningSpinner color="var(--color-fg-muted)" />}
    </span>
    <span>{children}</span>
  </div>
);

interface ListEmptyShellProps {
  /// Section icon, shown in the label row.
  icon: React.ReactNode;
  /// Section label, e.g. "Datasets".
  label: string;
  /// First-run hint, e.g. "Create a dataset to group related tables." Takes a
  /// node so a page whose first step is elsewhere can link to it.
  hint: React.ReactNode;
  /// Omit both to render no footer (e.g. Trash has no add action).
  addLabel?: string;
  onAdd?: () => void;
  /// Row-menu trigger props for the add pill, so the empty state offers the same
  /// create variants as the populated list's footer pill.
  addMenuTriggerProps?: RowContextMenu["triggerProps"];
  /// Until the first fetch lands: a spinner where the hint goes, the add pill still.
  loading?: boolean;
  search: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
  };
}

/// The zero-items view of the Overview-family list pages: the populated list's
/// own shell around one muted hint, so nothing jumps when the first item lands.
export const ListEmptyShell: React.FC<ListEmptyShellProps> = ({
  icon,
  label,
  hint,
  addLabel,
  onAdd,
  addMenuTriggerProps,
  loading,
  search,
}) => (
  <>
    <div className="shrink-0 mb-6">
      <SearchBar
        value={search.value}
        onChange={search.onChange}
        placeholder={search.placeholder}
        size="lg"
      />
    </div>

    {/* Only the list scrolls; the footer below stays pinned and its pulse
        ring is never clipped by this container's overflow. */}
    <div className="min-h-0 overflow-y-auto">
      <div className="mb-8">
        <div className="sticky top-0 z-10 bg-bg overflow-hidden">
          <SectionRule inset icon={icon} label={label} />
        </div>
        <HintRow loading={loading}>
          {loading ? `Loading ${label.toLowerCase()}…` : hint}
        </HintRow>
      </div>
    </div>

    {onAdd && addLabel && (
      <div className="shrink-0 flex items-center mt-6">
        <span className={loading ? "ml-auto" : "ml-auto pulse-hint"}>
          <MetaAddButton
            label={addLabel}
            onClick={onAdd}
            menuTriggerProps={addMenuTriggerProps}
          />
        </span>
      </div>
    )}
  </>
);
